import { useEffect, useCallback } from "react";
import { useTransactionsStore } from "../stores/transactionsStore";
import { useSDKConfigStore } from "../stores/sdkConfigStore";
import { useCardStatusStore } from "../stores/cardStatusStore";
import { getTransactions, getPendingTransactionBalance } from "../api/api";
import { Transaction } from "../api/types";

const PAGE_LIMIT = 20;

interface UseGetTransactionsReturn {
  transactions: Transaction[];
  isLoading: boolean;
  loadingNextPage: boolean;
  error: string | null;
  hasNext: boolean;
  pendingTxnAmount: number;
  loadMore: () => Promise<void>;
  refetch: () => Promise<void>;
  resetError: () => void;
}

// Helper function to handle API calls with consistent error handling and loading states
const executeTransactionFetch = async (
  apiCall: () => Promise<any>,
  onSuccess: (response: any) => void,
  errorMessage: string = "Failed to fetch transactions",
  isNextPage: boolean = false
) => {
  const store = useTransactionsStore.getState();

  try {
    if (isNextPage) {
      store._setLoadingNextPage(true);
    } else {
      store._setLoading(true);
    }
    store._setError(null);

    const response = await apiCall();
    onSuccess(response);
  } catch (error) {
    console.error(`[useGetTransactions] ${errorMessage}:`, error);
    const message = error instanceof Error ? error.message : errorMessage;
    store._setError(message);
  } finally {
    if (isNextPage) {
      store._setLoadingNextPage(false);
    } else {
      store._setLoading(false);
    }
  }
};

// Helper function to create API call function
const createApiCall =
  (cardID: string, limit: number = PAGE_LIMIT, cursor?: string) =>
  () =>
    getTransactions(cardID, limit, cursor);

// Helper function to handle initial fetch success
const handleInitialFetchSuccess = (response: any) => {
  const store = useTransactionsStore.getState();
  store._setTransactions(response.data.transactions);
  store._setPagination(response.data.pagination);
  // Note: initialFetchDone is now set in executeTransactionFetch finally block
};

// Helper function to handle load more success
const handleLoadMoreSuccess = (response: any) => {
  const store = useTransactionsStore.getState();
  store._appendTransactions(response.data.transactions);
  store._setPagination(response.data.pagination);
};

/**
 * Hook for managing transactions
 * Automatically fetches transactions on mount using SDK internal state
 * Provides pagination and onchain details functionality
 */
export const useGetTransactions = (): UseGetTransactionsReturn => {
  const {
    transactions,
    pendingTxnAmount,
    pagination,
    isLoading,
    loadingNextPage,
    error,
    initialFetchDone,
  } = useTransactionsStore();
  const { jwt } = useSDKConfigStore();
  const { userCard } = useCardStatusStore();

  const eoaAddress = jwt?.eoa || null;

  // Auto-fetch on initial mount
  useEffect(() => {
    const fetchInitialTransactions = async () => {
      if (userCard?.cardID && !initialFetchDone && !isLoading) {
        // Set initialFetchDone immediately to prevent race condition
        const store = useTransactionsStore.getState();
        store._setInitialFetchDone(true);

        const apiCall = createApiCall(userCard.cardID);
        await executeTransactionFetch(
          apiCall,
          handleInitialFetchSuccess,
          "Failed to fetch initial transactions"
        );
      }
    };

    const fetchPendingTransactionAmount = async () => {
      if (eoaAddress && userCard?.cardID && !isLoading) {
        try {
          const response = await getPendingTransactionBalance(userCard.cardID);
          const store = useTransactionsStore.getState();
          store._setPendingTxnAmount(response.data.balances.USD / 100);
        } catch (error) {
          console.error(
            "[useGetTransactions] Failed to fetch pending transaction amount:",
            error
          );
          const message =
            error instanceof Error
              ? error.message
              : "Failed to fetch pending transaction amount";
          const store = useTransactionsStore.getState();
          store._setError(message);
        }
      }
    };

    fetchInitialTransactions();
    fetchPendingTransactionAmount();
  }, [eoaAddress, userCard?.cardID, initialFetchDone, isLoading]);

  const loadMore = useCallback(async () => {
    if (
      !pagination?.has_next ||
      !pagination.next_cursor ||
      isLoading ||
      loadingNextPage ||
      !eoaAddress ||
      !userCard?.cardID
    ) {
      return;
    }

    const apiCall = createApiCall(
      userCard.cardID,
      PAGE_LIMIT,
      pagination.next_cursor
    );
    await executeTransactionFetch(
      apiCall,
      handleLoadMoreSuccess,
      "Failed to load more transactions",
      true
    );
  }, [pagination, isLoading, loadingNextPage, userCard?.cardID]);

  const refetch = useCallback(async () => {
    if (!userCard?.cardID || isLoading) return;

    const apiCall = createApiCall(userCard.cardID);
    await executeTransactionFetch(
      apiCall,
      handleInitialFetchSuccess,
      "Failed to refetch transactions"
    );
  }, [userCard?.cardID, isLoading]);

  // Function to reset error state
  const resetError = useCallback(() => {
    const store = useTransactionsStore.getState();
    store._setError(null);
  }, []);

  return {
    transactions,
    isLoading,
    loadingNextPage,
    error,
    hasNext: pagination?.has_next ?? false,
    pendingTxnAmount,
    loadMore,
    refetch,
    resetError,
  };
};
